import { useEffect, useState } from "react"
import { Dayjs } from "dayjs"
import { DAYS_OF_WEEK } from "config"
import { MealType, ScheduleableFoodType } from "model/food/types"
import { IScheduleConfigPayload } from "model/food/types/schedulePayloads.types"
import getSchedulePayloadFromFood from "model/food/utils/getPayloads/getSchedulePayloadFromFood"
import getSchedulePayloadFromMeal from "model/food/utils/getPayloads/getSchedulePayloadFromMeal"
import {
  IScheduleConfig,
  ScheduleDayOfWeekType,
  ScheduleType,
} from "model/food/types/schedule.types"
import { useSetMealTypeParams } from "features/meal/hooks"

const getDefaultConfig = () => {
  const config: IScheduleConfig = {
    type: "DAILY",
    startDate: "",
    endDate: "",
    daysOfWeek: [],
  }

  return config
}

const useScheduleFormSection = (initConfig?: IScheduleConfig) => {
  const [scheduleConfig, setScheduleConfig] = useState(
    initConfig ?? getDefaultConfig(),
  )
  const [isNoEndDate, setIsNoEndDate] = useState(
    initConfig?.endDate ? !initConfig.endDate : false,
  )

  useEffect(() => {
    if (initConfig) {
      setScheduleConfig(initConfig)
      setIsNoEndDate(!initConfig.endDate)
    }
  }, [initConfig])

  const onNoEndDateChange = (checked: boolean) => {
    setIsNoEndDate(checked)
    if (checked) {
      setScheduleConfig({
        ...scheduleConfig,
        endDate: "",
      })
    }
  }

  const onScheduleTypeChange = (value: string) => {
    const nextScheduleType = value.toUpperCase() as ScheduleType
    if (scheduleConfig) {
      setScheduleConfig({
        ...scheduleConfig,
        type: nextScheduleType,
      })
    }
  }

  const onSchedulePeriodChange = (period?: { start: Dayjs; end?: Dayjs }) => {
    if (scheduleConfig) {
      setScheduleConfig({
        ...scheduleConfig,
        startDate: period?.start.format() ?? "",
        endDate: period?.end?.format() ?? "",
      })
    }
  }

  const onDayOfWeekChange = (day: string) => {
    if (scheduleConfig) {
      let daysOfWeek = scheduleConfig.daysOfWeek

      const index = daysOfWeek.indexOf(day as ScheduleDayOfWeekType)
      if (index === -1) {
        daysOfWeek = [...daysOfWeek, day as ScheduleDayOfWeekType]
      } else {
        daysOfWeek = daysOfWeek.filter((dayOfWeek) => dayOfWeek !== day)
      }

      const shortedDaysOfWeek = DAYS_OF_WEEK.filter((day) =>
        daysOfWeek.includes(day),
      )
      setScheduleConfig({
        ...scheduleConfig,
        daysOfWeek: shortedDaysOfWeek,
      })
    }
  }

  const getScheduleConfigPayload = (): IScheduleConfigPayload => {
    switch (scheduleConfig.type) {
      case "DAILY":
        return {
          type: scheduleConfig.type,
        }

      case "WEEKLY":
        return {
          type: scheduleConfig.type,
          daysOfWeek: scheduleConfig.daysOfWeek,
        }

      case "CUSTOM":
        const formattedStartDate = scheduleConfig.startDate.slice(0, 10)
        const formattedEndDate = isNoEndDate
          ? undefined
          : scheduleConfig.endDate?.slice(0, 10)

        return {
          type: scheduleConfig.type,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          daysOfWeek: scheduleConfig.daysOfWeek,
        }
    }
  }

  const { mealTypeValue } = useSetMealTypeParams()

  const getCreateSchedulePayloadFromFood = (food: ScheduleableFoodType) => {
    const scheduleConfigPayload = getScheduleConfigPayload()
    const payload = getSchedulePayloadFromFood({
      food,
      configPayload: scheduleConfigPayload,
      mealType: mealTypeValue,
    })

    if (payload) {
      payload.isManualNutrition = food.isManualNutrition
    }

    return payload
  }

  const getCreateSchedulePayloadFromMeal = (meal: MealType) => {
    const scheduleConfigPayload = getScheduleConfigPayload()
    const payload = getSchedulePayloadFromMeal({
      meal,
      configPayload: scheduleConfigPayload,
      mealType: meal.type,
    })

    if (payload) {
      payload.isManualNutrition = meal.isManualNutrition
    }

    return payload
  }

  const getUpdateSchedulePayload = (meal: MealType) => {
    const scheduleConfigPayload = getScheduleConfigPayload()
    const mealPayload = getSchedulePayloadFromMeal({
      meal,
      configPayload: scheduleConfigPayload,
      mealType: meal.type,
    })

    if (mealPayload) {
      mealPayload.isManualNutrition = meal.isManualNutrition
    }

    return mealPayload
  }

  return {
    onScheduleTypeChange,
    onSchedulePeriodChange,
    onDayOfWeekChange,
    onNoEndDateChange,
    getUpdateSchedulePayload,
    scheduleConfig,
    isNoEndDate,
    getCreateSchedulePayloadFromFood,
    getCreateSchedulePayloadFromMeal,
  }
}

export default useScheduleFormSection
